import {
  getLeoUuid,
  LeoUUID,
  getObject,
  getBoolean,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { UserName, UserNameDTO } from "../types/userName";
/* eslint-disable import/export */
export abstract class UpdateSpocNameAndSettingsRPC implements
  LeoRPC<UpdateSpocNameAndSettingsRPC.Request, UpdateSpocNameAndSettingsRPC.Response, UpdateSpocNameAndSettingsRPC.Errors.Errors>
{
  abstract execute(
    request: UpdateSpocNameAndSettingsRPC.Request
  ): Promise<LeoRPCResult<UpdateSpocNameAndSettingsRPC.Response, UpdateSpocNameAndSettingsRPC.Errors.Errors>>;
}

export namespace UpdateSpocNameAndSettingsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    spocId: string;
    userName: UserNameDTO;
    isDownloadingSurveyReportAllowed: boolean;
    isViewingIndividualSurveyResponseAllowed: boolean;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isUpdateSpocNameAndSettingsRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly spocId: LeoUUID;
    readonly userName: UserName;
    readonly isDownloadingSurveyReportAllowed: boolean;
    readonly isViewingIndividualSurveyResponseAllowed: boolean;
  
  
    constructor(
    projectId: LeoUUID, 
    spocId: LeoUUID, 
    userName: UserName, 
    isDownloadingSurveyReportAllowed: boolean, 
    isViewingIndividualSurveyResponseAllowed: boolean
    ) {
      this.projectId = projectId;
      this.spocId = spocId;
      this.userName = userName;
      this.isDownloadingSurveyReportAllowed = isDownloadingSurveyReportAllowed;
      this.isViewingIndividualSurveyResponseAllowed = isViewingIndividualSurveyResponseAllowed;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const spocId = getLeoUuid(dto, "spocId", InvalidRequestSpocIdError);
      const userName = UserName.fromDTO(getObject(dto, "userName", InvalidRequestUserNameError));
      const isDownloadingSurveyReportAllowed = getBoolean(dto, "isDownloadingSurveyReportAllowed", InvalidRequestIsDownloadingSurveyReportAllowedError);
      const isViewingIndividualSurveyResponseAllowed = getBoolean(dto, "isViewingIndividualSurveyResponseAllowed", InvalidRequestIsViewingIndividualSurveyResponseAllowedError);
      return new Request(
        projectId,
        spocId,
        userName,
        isDownloadingSurveyReportAllowed,
        isViewingIndividualSurveyResponseAllowed
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          spocId: this.spocId.toDTO(),
          userName: this.userName.toDTO(),
          isDownloadingSurveyReportAllowed: this.isDownloadingSurveyReportAllowed,
          isViewingIndividualSurveyResponseAllowed: this.isViewingIndividualSurveyResponseAllowed
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `UpdateSpocNameAndSettingsRPC(projectId='${this.projectId}',spocId='${this.spocId}',userName='${this.userName}',isDownloadingSurveyReportAllowed='${this.isDownloadingSurveyReportAllowed}',isViewingIndividualSurveyResponseAllowed='${this.isViewingIndividualSurveyResponseAllowed}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `UpdateSpocNameAndSettingsRPC.Response()`;
  };

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    InvalidSpocId = "INVALID_SPOC_ID",
    UnableToUpdateUsername = "UNABLE_TO_UPDATE_USERNAME"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `UpdateSpocNameAndSettingsRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `UpdateSpocNameAndSettingsRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface InvalidSpocIdDTO {
      code: string;
    }
    
    export class InvalidSpocId extends LeoRPCError<RPCError> {
      readonly isInvalidSpocId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSpocId;
        super(code);
      }
    
    
      toDTO(): InvalidSpocIdDTO {
        const dto: InvalidSpocIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSpocId.prototype.toString = function toString(): string {
      return `UpdateSpocNameAndSettingsRPC.INVALID_SPOC_ID()`;
    };
    
    
    export interface UnableToUpdateUsernameDTO {
      code: string;
    }
    
    export class UnableToUpdateUsername extends LeoRPCError<RPCError> {
      readonly isUnableToUpdateUsername: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.UnableToUpdateUsername;
        super(code);
      }
    
    
      toDTO(): UnableToUpdateUsernameDTO {
        const dto: UnableToUpdateUsernameDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    UnableToUpdateUsername.prototype.toString = function toString(): string {
      return `UpdateSpocNameAndSettingsRPC.UNABLE_TO_UPDATE_USERNAME()`;
    };
    

    export type Errors = InvalidProjectId | ProjectAlreadyArchived | InvalidSpocId | UnableToUpdateUsername;
    export type ErrorsDTO = InvalidProjectIdDTO | ProjectAlreadyArchivedDTO | InvalidSpocIdDTO | UnableToUpdateUsernameDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.InvalidSpocId:
          return new InvalidSpocId();
        case RPCError.UnableToUpdateUsername:
          return new UnableToUpdateUsername();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends UpdateSpocNameAndSettingsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestSpocIdError extends UpdateSpocNameAndSettingsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSpocIdError";
  }
}

export class InvalidRequestUserNameError extends UpdateSpocNameAndSettingsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestUserNameError";
  }
}

export class InvalidRequestIsDownloadingSurveyReportAllowedError extends UpdateSpocNameAndSettingsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestIsDownloadingSurveyReportAllowedError";
  }
}

export class InvalidRequestIsViewingIndividualSurveyResponseAllowedError extends UpdateSpocNameAndSettingsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestIsViewingIndividualSurveyResponseAllowedError";
  }
}


export class InvalidResponseInvalidProjectIdError extends UpdateSpocNameAndSettingsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends UpdateSpocNameAndSettingsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseInvalidSpocIdError extends UpdateSpocNameAndSettingsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSpocIdError";
  }
}

export class InvalidResponseUnableToUpdateUsernameError extends UpdateSpocNameAndSettingsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseUnableToUpdateUsernameError";
  }
}

